import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Col,
  Row,
  Card,
  CardBody,
  Input,
  Button,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
  FormFeedback,
  Alert
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import toastr from "toastr";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import TableContainer from "../../components/Common/TableContainer";
import Paginations from "../../components/Common/Pagination";
import Spinners from "../../components/Common/Spinner";
import { resolvePaginatedList } from "../../helpers/paginatedResponse";
import { useGetAllNewsLetter, useUpdateNewsLetter } from "../../api/news-letter";

const NewsLetterAll = () => {
  document.title = "Newsletter Subscribers | Admin";

  // server-side controls
  const [page, setPage] = useState(1); // 1-based
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  // Modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSubscriber, setEditingSubscriber] = useState(null);

  // mutation hook (GET)
  const { mutate, data, isLoading, isPending, error } = useGetAllNewsLetter();
  const updateMutation = useUpdateNewsLetter();
  const loading = isLoading || isPending;

  const refetch = useCallback(() => {
    mutate({ page, limit, search: search.trim() || undefined });
  }, [mutate, page, limit, search]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const { list: rows, total } = resolvePaginatedList(data);

  // Edit modal handlers
  const handleEdit = useCallback((subscriber) => {
    setEditingSubscriber(subscriber);
    setShowEditModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowEditModal(false);
    setEditingSubscriber(null);
  }, []);

  // Edit formik
  const editFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      status: editingSubscriber?.status || "",
      descriptive: editingSubscriber?.descriptive || ""
    },
    validationSchema: Yup.object({
      status: Yup.string()
        .oneOf(["in-progress", "resolved"], "Status must be either 'in-progress' or 'resolved'")
        .required("Status is required"),
      descriptive: Yup.string().required("Descriptive field is required")
    }),
    onSubmit: (values) => {
      const subscriberId =
        editingSubscriber?.id || editingSubscriber?.slug || editingSubscriber?.subscriberId;
      if (!subscriberId) return;

      updateMutation.mutate(
        {
          subscriberId: subscriberId,
          payload: {
            status: values.status,
            descriptive: values.descriptive
          }
        },
        {
          onSuccess: () => {
            toastr.success("Subscriber updated successfully");
            handleCloseModal();
            refetch();
          }
        }
      );
    }
  });

  const columns = useMemo(
    () => [
      { header: "Name", accessorKey: "fullName", enableColumnFilter: false },
      {
        header: "Email",
        accessorKey: "email",
        enableColumnFilter: false,
        cell: (ctx) => {
          const email = ctx.row.original.email ?? "";
          return email ? (
            <a href={`mailto:${email}`} rel="noopener noreferrer">
              {email}
            </a>
          ) : (
            "-"
          );
        }
      },
      { header: "Phone", accessorKey: "phoneNumber", enableColumnFilter: false },
      {
        header: "Role",
        accessorKey: "role",
        enableColumnFilter: false,
        cell: (info) => info.getValue() || "-"
      },
      {
        header: "Created At",
        accessorKey: "createdAt",
        enableColumnFilter: false,
        cell: (info) => (info.getValue() ? new Date(info.getValue()).toLocaleDateString() : "-")
      },
      {
        header: "Status",
        accessorKey: "status",
        enableColumnFilter: false,
        cell: (ctx) => {
          const status = ctx.row.original?.status || "-";
          if (status === "-") return "-";
          const badgeColor =
            status === "resolved" ? "success" : status === "in-progress" ? "warning" : "secondary";
          return <Badge className={`bg-${badgeColor}`}>{status}</Badge>;
        }
      },
      {
        header: "Descriptive",
        accessorKey: "descriptive",
        enableColumnFilter: false,
        cell: (ctx) => {
          const descriptive = ctx.row.original?.descriptive || "-";
          return descriptive.length > 50 ? `${descriptive.substring(0, 50)}...` : descriptive;
        }
      },
      {
        header: "Action",
        accessorKey: "action",
        enableColumnFilter: false,
        cell: (ctx) => {
          return (
            <Button size="sm" color="primary" onClick={() => handleEdit(ctx.row.original)}>
              <i className="mdi mdi-pencil me-1" />
              Edit
            </Button>
          );
        }
      }
    ],
    [handleEdit]
  );

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="News Letter" breadcrumbItem="News Letter" />

        <Row className="mb-3 g-2 align-items-center">
          {/* Search */}
          <Col md={4}>
            <Input
              type="text"
              placeholder="Search by name, email, phone..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setPage(1);
                  setSearch(searchInput.trim());
                }
              }}
            />
          </Col>
        </Row>

        <Row className="mb-3 g-2 align-items-center">
          <Col md="auto" className="ms-auto d-flex gap-2 align-items-center">
            <Button
              color="primary"
              onClick={() => {
                setPage(1);
                setSearch(searchInput.trim());
              }}
              disabled={loading}
            >
              <i className="mdi mdi-magnify me-1" />
              Search
            </Button>
            <Button
              color="secondary"
              outline
              onClick={() => {
                setSearch("");
                setSearchInput("");
                setLimit(10);
                setPage(1);
              }}
              disabled={loading}
            >
              Reset
            </Button>
            {/* Page size */}
            <Input
              type="select"
              value={limit}
              onChange={(e) => {
                setLimit(Number(e.target.value));
                setPage(1);
              }}
              style={{ width: "auto", minWidth: "100px" }}
            >
              {[10, 20].map((n) => (
                <option key={n} value={n}>
                  Show {n}
                </option>
              ))}
            </Input>
            <Button color="light" onClick={refetch} disabled={loading}>
              <i className="mdi mdi-refresh" />
            </Button>
          </Col>
        </Row>

        <Card>
          <CardBody>
            {loading && rows.length === 0 ? (
              <Spinners setLoading={() => {}} />
            ) : error ? (
              <Alert color="danger" className="mb-0">
                {error?.response?.data?.message || error?.message || "Failed to load subscribers."}
              </Alert>
            ) : (
              <>
                <TableContainer
                  columns={columns}
                  data={rows}
                  isPagination={false} // server-side
                  isCustomPageSize={false}
                  isGlobalFilter={false}
                  isJobListGlobalFilter={false}
                  tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-check dataTable no-footer dtr-inline mt-2 border-top"
                  theadClass="table-light"
                  divClassName="table-responsive"
                />
                <Paginations
                  total={total}
                  perPageData={limit}
                  currentPage={page}
                  setCurrentPage={setPage}
                  isShowingPageLength
                  paginationDiv="dataTables_paginate paging_simple_numbers pagination-rounded d-flex justify-content-end mt-3"
                  paginationClass="pagination"
                />
              </>
            )}
          </CardBody>
        </Card>

        {/* Edit Modal */}
        <Modal isOpen={showEditModal} toggle={handleCloseModal} size="lg">
          <ModalHeader toggle={handleCloseModal}>Update Newsletter Subscriber</ModalHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              editFormik.handleSubmit();
              return false;
            }}
          >
            <ModalBody>
              <Row>
                <Col md={12} className="mb-3">
                  <Label className="form-label">Status *</Label>
                  <Input
                    type="select"
                    name="status"
                    value={editFormik.values.status}
                    onChange={editFormik.handleChange}
                    onBlur={editFormik.handleBlur}
                    invalid={editFormik.touched.status && !!editFormik.errors.status}
                  >
                    <option value="">Select Status</option>
                    <option value="in-progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                  </Input>
                  <FormFeedback>{editFormik.errors.status}</FormFeedback>
                </Col>

                <Col md={12}>
                  <Label className="form-label">Descriptive *</Label>
                  <Input
                    type="textarea"
                    rows="6"
                    name="descriptive"
                    placeholder="Enter descriptive information..."
                    value={editFormik.values.descriptive}
                    onChange={editFormik.handleChange}
                    onBlur={editFormik.handleBlur}
                    invalid={editFormik.touched.descriptive && !!editFormik.errors.descriptive}
                  />
                  <FormFeedback>{editFormik.errors.descriptive}</FormFeedback>
                </Col>
              </Row>
            </ModalBody>
            <ModalFooter>
              <Button
                type="button"
                color="secondary"
                onClick={handleCloseModal}
                disabled={updateMutation.isPending}
              >
                Cancel
              </Button>
              <Button type="submit" color="primary" disabled={updateMutation.isPending}>
                {updateMutation.isPending ? "Updating..." : "Update"}
              </Button>
            </ModalFooter>
          </form>
        </Modal>
      </div>
    </div>
  );
};

export default NewsLetterAll;
